import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core";

import MapRegion from "../icons/map-region.svg";
import MapArea from "../icons/map-area.svg";
import MapGpsTerritory from "../icons/map-gps-territory.svg";
import WorkOfficeBuilding from "../icons/work-office-building.svg";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BounceLoader from "react-spinners/BounceLoader";
import useNoParamFetch from "../../RevampedDashboard/hooks/useNoParamFetch";
import useOnlyParamFetch from "../../RevampedDashboard/hooks/useOnlyParamFetch";
import DashboardWidgetCard from "../../RevampedDashboard/components/DashboardWidgetCard";
import DashboardCardHeader from "../../RevampedDashboard/components/DashboardCardHeader";
import CustomSelect from "../../RevampedDashboard/components/CustomSelect";

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: "white",
    zIndex: 40,
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "rgba(54, 43, 115, 1)",
    margin: theme.spacing(0, 1.5),
    "& h1": {
      fontSize: 24,
      fontWeight: 700,
      margin: 0,
    },
    "& p": {
      fontSize: 14,
      fontWeight: 500,
      margin: 0,
    },
  },
  filtersContent: {
    display: "flex",
    backgroundColor: "rgba(248, 249, 252, 1)",
    borderRadius: 8,
    padding: theme.spacing(1),
    [theme.breakpoints.down("md")]: {
      flexWrap: "wrap",
    },
  },
  headerActions: {
    display: "flex",
    color: "rgba(226, 19, 110, 1)",
    "& p": {
      margin: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
    },
  },
  separatorLine: {
    margin: theme.spacing(0, 2),
    border: "0.5px solid rgba(0, 14, 61, 0.08)",
  },
  filterWrapper: {
    width: "25%",
    [theme.breakpoints.down("md")]: {
      width: "50%",
    },
  },
}));

export default function GeographyFilters({
  clusters,
  agency,
  region,
  area,
  territory,
  onFilterChange,
  label = "Merchant Loan Requests",
}) {
  const [clusterLocal, setClusterLocal] = useState(clusters);
  const [agencyLocal, setAgencyLocal] = useState(agency);
  const [regionLocal, setRegionLocal] = useState(region);
  const [areaLocal, setAreaLocal] = useState(area);
  const [territoryLocal, setTerritoryLocal] = useState(territory);

  const clusterParams = useMemo(() => {
    return clusterLocal?.length > 0 ? { clusters: clusterLocal } : null;
  }, [clusterLocal]);
  const regionParams = useMemo(() => {
    return regionLocal?.length > 0 ? { region: regionLocal } : null;
  }, [regionLocal]);

  const areaParams = useMemo(() => {
    return areaLocal?.length > 0 ? { area: areaLocal } : null;
  }, [areaLocal]);

  const territoryParams = useMemo(() => {
    return territoryLocal?.length > 0 ? { territory: territoryLocal } : null;
  }, [territoryLocal]);
  const { data: clusterData } = useNoParamFetch(
    "/organizations/api/v1/clusters"
  );
  const { data: regionData } = useOnlyParamFetch(
    "/organizations/api/v1/regions",
    clusterParams
  );
  const { data: areaData } = useOnlyParamFetch(
    "/organizations/api/v1/areas",
    regionParams ? regionParams : clusterParams
  );

  const { data: territoriesData } = useOnlyParamFetch(
    "/organizations/api/v1/territories",
    areaParams ? areaParams : regionParams ? regionParams : clusterParams
  );

  const { data: agencyData } = useOnlyParamFetch(
    "/organizations/api/v1/agency",
    territoryParams
      ? territoryParams
      : areaParams
      ? areaParams
      : regionParams
      ? regionParams
      : clusterParams
  );
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const classes = useStyles();

  const clearFilters = () => {
    onFilterChange({
      clusters: [],
      agency: [],
      region: [],
      area: [],
      territory: [],
    });
    setClusterLocal([]);
    setAgencyLocal([]);
    setAreaLocal([]);
    setRegionLocal([]);
    setTerritoryLocal([]);
  };

  const applyFilters = () => {
    onFilterChange({
      clusters: clusterLocal,
      agency: agencyLocal,
      region: regionLocal,
      area: areaLocal,
      territory: territoryLocal,
    });
  };

  return (
    <DashboardWidgetCard className={classes.card}>
      <DashboardCardHeader
        label={label}
        styles={{
          labelClass: {
            color: "#362B73CC",
          },
        }}
      >
        <div className={classes.headerActions}>
          <p onClick={applyFilters}>Apply Filters</p>
          <div className={classes.separatorLine}></div>
          <p onClick={clearFilters}>Clear Filters</p>
          <div className={classes.separatorLine}></div>
          <p onClick={() => setIsFiltersCollapsed((prev) => !prev)}>
            {isFiltersCollapsed ? "Expand" : "Collapse"}
            {isFiltersCollapsed ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowUpIcon fontSize="small" />
            )}
          </p>
        </div>
      </DashboardCardHeader>
      {!isFiltersCollapsed && (
        <div className={classes.filtersContent}>
          <div className={classes.filterWrapper}>
            {clusterData ? (
              <CustomSelect
                item={{
                  label: "Circle",
                  key: "clusters",
                  options: clusterData,
                  value: clusterLocal,
                  category: "geo_filter",
                  icon: MapRegion,
                }}
                handleChange={(e) => {
                  setClusterLocal(e.target.value);
                  setRegionLocal([]);
                  setAgencyLocal([]);
                  setAreaLocal([]);
                  setTerritoryLocal([]);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {regionData ? (
              <CustomSelect
                item={{
                  label: "Zone",
                  key: "region",
                  options: regionData,
                  value: regionLocal,
                  category: "geo_filter",
                  icon: MapRegion,
                }}
                handleChange={(e) => {
                  setRegionLocal(e.target.value);
                  setAgencyLocal([]);
                  setAreaLocal([]);
                  setTerritoryLocal([]);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {areaData ? (
              <CustomSelect
                item={{
                  label: "Area",
                  key: "area",
                  options: areaData,
                  value: areaLocal,
                  category: "geo_filter",
                  icon: MapArea,
                }}
                handleChange={(e) => {
                  setAreaLocal(e.target.value);
                  setAgencyLocal([]);
                  setTerritoryLocal([]);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {territoriesData ? (
              <CustomSelect
                item={{
                  label: "Territory",
                  key: "territory",
                  options: territoriesData,
                  value: territoryLocal,
                  category: "geo_filter",
                  icon: MapGpsTerritory,
                }}
                handleChange={(e) => {
                  setTerritoryLocal(e.target.value);
                  setAgencyLocal([]);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {agencyData ? (
              <CustomSelect
                item={{
                  label: "Distributor",
                  key: "distributor",
                  options: agencyData,
                  value: agencyLocal,
                  category: "geo_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setAgencyLocal(e.target.value);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
        </div>
      )}
      {/* //<div style={{ height: 40 }}></div> */}
    </DashboardWidgetCard>
  );
}
